import { toUSE164 } from '@fruitful/fruitils';
import type { GridApi } from 'ag-grid-community';
import { format } from 'date-fns';

import type { MembershipSummary } from '../../entityTypes';

type MembershipUser = MembershipSummary['users'][number];

interface MemberCsvRow {
    isMembershipActive: boolean;
    guideId: string | null;
    memberName: string;
    memberEmail: string;
    membershipType: string;
    createdAt: string;
    stripeProductName: string;
    memberJourneyStatus: string;
    nextSession: string;
    previousSession: string;
    membershipId: string;
    memberId: string;
    hasIM: string;
    memberPhone: string;
    memberState: string;
}

interface MemberCsvColumn {
    headerName: string;
    getValue: (row: MemberCsvRow) => string;
}

interface ExportMembersCsvParams {
    gridApi: GridApi<MembershipSummary>;
}

export const getTimestampedCsvFileName = (prefix: string): string => {
    return `${prefix}-${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}.csv`;
};

const MEMBER_EXPORT_FIELD_GETTERS: Record<
    string,
    (row: MemberCsvRow) => string
> = {
    isMembershipActive: (row) => String(row.isMembershipActive),
    guideId: (row) => row.guideId ?? '',
    membersName: (row) => row.memberName,
    membersEmail: (row) => row.memberEmail,
    membershipType: (row) => row.membershipType,
    createdAt: (row) => row.createdAt,
    stripeProductName: (row) => row.stripeProductName,
    memberJourneyStatus: (row) => row.memberJourneyStatus,
    nextSession: (row) => row.nextSession,
    previousSession: (row) => row.previousSession,
    id: (row) => row.membershipId,
    membersId: (row) => row.memberId,
    hasIM: (row) => row.hasIM,
    membersPhone: (row) => row.memberPhone,
    membersState: (row) => row.memberState,
};

const getMemberDisplayName = (member: MembershipUser): string => {
    const firstName = member.firstName?.trim() ?? '';
    const lastName = member.lastName?.trim() ?? '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || '[not provided yet]';
};

const membershipToMemberCsvRows = (
    membership: MembershipSummary,
): MemberCsvRow[] => {
    const membershipFields = {
        isMembershipActive: membership.isMembershipActive,
        guideId: membership.guideId,
        membershipType: membership.membershipType,
        createdAt: membership.createdAt,
        stripeProductName: membership.stripeProductName ?? '',
        memberJourneyStatus: membership.memberJourneyStatus,
        nextSession: membership.nextSession,
        previousSession: membership.previousSession,
        membershipId: membership.id,
        hasIM: membership.hasIM,
    };

    if (membership.users.length === 0) {
        return [
            {
                ...membershipFields,
                memberName: '',
                memberEmail: '',
                memberId: '',
                memberPhone: '',
                memberState: '',
            },
        ];
    }

    return membership.users.map((member) => ({
        ...membershipFields,
        memberName: getMemberDisplayName(member),
        memberEmail: member.email,
        memberId: member.id,
        memberPhone: toUSE164(member.phoneNumber) ?? '',
        memberState: (member.userAddress?.state ?? '').trim(),
    }));
};

const getMemberCsvColumnsFromGrid = (
    gridApi: GridApi<MembershipSummary>,
): MemberCsvColumn[] => {
    return gridApi
        .getAllDisplayedColumns()
        .map((column) => column.getColDef())
        .flatMap((colDef) => {
            const field = colDef.field;
            if (field == null) {
                return [];
            }

            const getValue = MEMBER_EXPORT_FIELD_GETTERS[field];
            if (getValue == null) {
                return [];
            }

            return [
                {
                    headerName: colDef.headerName ?? field,
                    getValue,
                },
            ];
        });
};

const escapeCsvCell = (value: string): string => {
    if (/[",\n\r]/.test(value)) {
        return `"${value.replace(/"/g, '""')}"`;
    }

    return value;
};

const buildCsv = (columns: MemberCsvColumn[], rows: MemberCsvRow[]): string => {
    const header = columns.map((column) => escapeCsvCell(column.headerName));
    const dataRows = rows.map((row) =>
        columns.map((column) => escapeCsvCell(column.getValue(row))),
    );

    return [header, ...dataRows].map((cells) => cells.join(',')).join('\n');
};

const downloadCsv = (fileName: string, csvContent: string): void => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
};

export const exportMembersToCsv = ({
    gridApi,
}: ExportMembersCsvParams): void => {
    const columns = getMemberCsvColumnsFromGrid(gridApi);
    if (columns.length === 0) {
        return;
    }

    const rows: MemberCsvRow[] = [];

    gridApi.forEachNodeAfterFilterAndSort((node) => {
        if (node.data != null) {
            rows.push(...membershipToMemberCsvRows(node.data));
        }
    });

    const csvContent = buildCsv(columns, rows);
    downloadCsv(getTimestampedCsvFileName('members-export'), csvContent);
};
